/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect, useRef, useState } from 'react';

import CheckoutOrder from './CheckoutOrder';
import CheckoutTable, { CheckoutTableProps, TableProps } from './CheckoutTable';
import CountDownProgress from './CountDownProgress/CountDownProgress';
import BottomActionBar from './BottomActionBar';
import { Box } from './styles';

import { calculateStartAndEndTimes } from './utils/calculateStartAndEndTimes';
import { currencyFormatter, formattedNumber } from './utils/formatterHelper';
import useGetSenderAndReceiverDetails from './hooks/useGetSenderAndReceiverDetails';

import { ApplicationState } from '../../../../store';
import { useDispatch, useSelector } from 'react-redux';
import { formatDate } from '../../../../utils/formatDate';

import {
  ICheckoutResponse,
  ICustomerIdParams,
  IDataOrderSimulate,
  IEstimatedDateResponse,
  IItemParamOrderSimulate,
  IItemResponseOrderSimulate,
  IItemSaleInfoMvp,
  IOrderSimulateState,
  ISalesParams,
  ITaxesCalculateResponse
} from '../../../../store/ducks/order-simulate-checkout-mvp/types';
import { addToast } from '../../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../../store/ducks/toast/types';
import {
  convertKgToTon,
  convertToKg,
  formatTotalWeight
} from './utils/unitsOfMeasurement';
import { useHistory } from 'react-router-dom';
import LoadingModal from '../../../../components/LoadingModal';
import { ErrorModal } from './ErrorModal';
import { sendTagging } from '../../../../store/ducks/tagging/actions';
import { EOrderSettingsRoutes } from '../OrderSettingsMvp/orderSettings.routes';
import { updateCartItemsQuantity } from '../../../../store/ducks/cart-items-mvp/actions';
import { buildTaxesPayload, displayTaxes } from './utils/taxesHelper';
import { purchaseOrigin } from '../../../../services/MVP/utils';
import {
  getTipoCompraForItemId,
  getLastTipoCompra
} from '../../../../services/MVP/tipoCompraMap';
import { formatNumberWithLocale } from '../../../../utils/formatNumbers';
import { EOrdersRoutes } from '../../../Orders/orders.routes';
import { ERoutes } from '../../../../routes';
import { GetFlag } from '../../../../utils/featureFlag';
import { ILocality2 } from '../../../../store/commomTypes';
import { useCart } from '../ShoppingCartMVP/useCart';
import { NewCartItem } from '../../../../services/MVP/models';
import { useBulkErrorToast } from '../../../../services/MVP/useBulkErrorToast';
import { CheckoutServiceMvp } from '../../../../services/CheckoutMvp';
import moment from 'moment';
import { useCheckout } from '../../../../providers/checkout.provider';
import { CHECKOUT_ORDERS_INITIAL_STATE } from '../../../../store/ducks/order-simulate-checkout-mvp';

interface ICheckoutProps {
  title: string;
  value?: string;
}

const MODAL_ERROR_DEFAULT = {
  title: 'Serviço indisponível temporariamente',
  description:
    'Tente novamente mais tarde ou entre em contato com o seu vendedor.',
  buttonRight: {
    title: 'Fechar'
  },
  buttonLeft: {
    title: 'Cancelar pedido',
    canShow: false
  },
  show: false
};

type ItemWithAgreementPricing = IItemParamOrderSimulate & {
  isAgreementPricing: string;
};

const handleTaxesCalculation = async ({
  items,
  checkoutServiceMvp,
  receiverId,
  emitterId,
  cartId
}: {
  items: ItemWithAgreementPricing[];
  checkoutServiceMvp: CheckoutServiceMvp;
  receiverId: string;
  emitterId: string;
  cartId: string;
}) => {
  const taxesPayload = {
    cartNumber: cartId,
    purchaseNumber: '',
    shipTo: receiverId,
    soldTo: emitterId,
    items: items.map((item, index) => ({
      itemWeb: String(index + 1).padStart(2, '0'),
      activitySector: item.activitySector,
      benefFisc: item.benefit || '',
      consumptionOrder: item.consumptionOrder,
      distrChannel: item.distrChannel,
      industrialType: item.industrialType,
      item_id_gerdau: item.item_id_gerdau,
      material: item.material,
      plant: item.plant,
      price_agreement_date_gerdau: item.price_agreement_date_gerdau,
      quantity: item.quantity,
      requiredDate: item.requiredDate
        ? moment(item.requiredDate).format('YYYY-MM-DDTHH:mm:ss')
        : '',
      salesGroup: item.salesGroup,
      salesOffice: item.salesOffice,
      salesOrg: item.salesOrg,
      term: item.term,
      unityMeasure: item.unityMeasure,
      benefit: item.benefit,
      isAgreementPricing: item.isAgreementPricing
    }))
  };

  const taxesResult = await checkoutServiceMvp.calculateTaxes(taxesPayload);

  if (taxesResult instanceof Error) {
    throw taxesResult;
  }

  return taxesResult;
};

const handleEstimatedDate = async ({
  items,
  checkoutServiceMvp,
  receiverId,
  emitterId
}: {
  items: IDataOrderSimulate['items'];
  checkoutServiceMvp: CheckoutServiceMvp;
  receiverId: string;
  emitterId: string;
}) => {
  const estimatedDatePayload = {
    soldTo: emitterId,
    shipTo: receiverId,
    items: items.map((item, index) => ({
      item_web: String(index + 1).padStart(3, '0'),
      unityMeasure: item.unityMeasure,
      quantity: item.quantity,
      requiredDate: item?.requiredDate
        ? moment(item.requiredDate).format('YYYY-MM-DDTHH:mm:ss')
        : '',
      material: item.material,
      plant: String(item.plant),
      salesOffice: item.salesOffice,
      salesGroup: item.salesGroup,
      distrChannel: String(item.distrChannel),
      salesOrg: item.salesOrg,
      activitySector: item.activitySector,
      term: item.term
    }))
  };

  const estimatedDateResult = await checkoutServiceMvp.getEstimatedDate(
    estimatedDatePayload
  );

  if (estimatedDateResult instanceof Error) {
    throw estimatedDateResult;
  }

  return estimatedDateResult;
};

const CheckoutMVP: React.FC = () => {
  const dispatch = useDispatch();
  const { dispatchBulkErrorToast } = useBulkErrorToast();
  const tipo_compra = getLastTipoCompra(purchaseOrigin());
  const { errorOrdersSent, loadingOrdersSent, orderConfirmationRequested } =
    useSelector((state: ApplicationState) => state.orderSimulateCheckoutMvp);
  const { emitterSelected, receiverSelected } =
    useGetSenderAndReceiverDetails();
  const [data, setData] = useState<ICheckoutResponse | null>(null);
  const [params, setParams] = useState<IDataOrderSimulate | null>(null);
  const [checkoutState, setCheckoutState] =
    useState<IOrderSimulateState | null>(null);
  const history = useHistory();
  const orderInfoRef = useRef<HTMLDivElement | null>(null);
  const orderTableRef = useRef<HTMLDivElement | null>(null);
  const [tableIsValid, setTableIsValid] = useState(true);
  const [startTimeState, setStartTime] = useState<Date>();
  const [endTimeState, setEndTime] = useState<Date>();
  const [taxValues, setTaxValues] = useState<ICheckoutProps[] | null>(null);
  // const [orderDetails, setOrderDetails] = useState<ICheckoutProps[]>([]);
  const [totalValues, setTotalValues] = useState<ICheckoutProps[]>([]);
  const [ordersTable, setOrdersTable] = useState<CheckoutTableProps[]>([]);
  const [quantityOfItems, setQuantityOfItems] = useState<number>(0);
  const [itemsSalesInfo, setItemsSalesInfo] = useState<IItemSaleInfoMvp[]>([]);
  const [customerPurchaseId, setCustomerPurchaseId] = useState('');
  const [renderCount, setRenderCount] = useState(0);

  const { updateCheckoutData, checkoutData } = useCheckout();
  const taxesLoadedRef = useRef(false);
  const [taxesData, setTaxesData] = useState<{
    loading: boolean;
    error: boolean;
    data: ITaxesCalculateResponse | undefined;
    retryCount: number;
  }>({
    loading: false,
    error: false,
    data: undefined,
    retryCount: 0
  });

  const [estimatedDateData, setEstimatedDateData] = useState<{
    loading: boolean;
    error: boolean;
    data: IEstimatedDateResponse | undefined;
  }>({
    loading: false,
    error: false,
    data: undefined
  });

  const shoppingCart = useSelector(
    (appState: ApplicationState) => appState.longSteelShoppingCart
  );

  const { addBulkGetCart, cartId } = useCart();

  const desabilitarFinalizarPedido = GetFlag('desabilitar_finalizar_pedido');
  const newCheckoutFlow = GetFlag('new_checkout_flow');
  const abandonedCartFlag = GetFlag('abandoned_cart');
  const [isLoading, setIsLoading] = useState(false);
  const [modalError, setModalError] = useState(MODAL_ERROR_DEFAULT);

  const loading = loadingOrdersSent || isLoading;
  const buttonLoading =
    loading || taxesData.loading || estimatedDateData.loading;

  const currency = 'BRL';

  const goToPage = (pageUrl: string) => {
    history.push(pageUrl);
  };

  const goBack = () => {
    history.goBack();
  };

  const handleClickTagging = (eventName: string, screenName: string) => {
    dispatch(
      sendTagging({
        event: eventName,
        screenName: screenName,
        tipo_compra: tipo_compra,
        bp_emissor: emitterSelected?.customerId,
        bp_recebedor: receiverSelected?.customerId
      })
    );
  };
  useEffect(() => {
    const loadData = () => {
      try {
        const result = checkoutData;
        if (!result) {
          return;
        }
        if (result.orderCompleted) {
          history.replace(`${EOrderSettingsRoutes.SHOPPING_CART}`);
          return;
        }
        setCheckoutState(result);
        setData(result.data);
        setParams(result.params);
        setTimeout(() => {
          const displayWarnings = () => {
            if (result.data?.orders?.length && result.data.orders.length > 1) {
              dispatch(
                addToast({
                  message:
                    'Seu pedido foi separado em mais de uma ordem de venda.',
                  type: EToastTypes.INFO,
                  durationInMS: 5000
                })
              );
              handleClickTagging('modal_pedido_separado', 'revisar_pedido');
            }
            if (result.data?.orders?.length) {
              const itemWithoutDispDate = result.data.orders.some((table) =>
                table.order.some(
                  (order) =>
                    !order.extension_attributes.sap_fields.o_item_sap.disp_date
                      .length ||
                    !formatDate(
                      order.extension_attributes.sap_fields.o_item_sap.disp_date
                    ).length
                )
              );

              if (itemWithoutDispDate) {
                const itemWithoutDispDateTagging = result.data.orders.map(
                  (table) => {
                    table.order.filter(
                      (order) =>
                        order.extension_attributes.sap_fields.o_item_sap
                          .disp_date.length === 0 ||
                        formatDate(
                          order.extension_attributes.sap_fields.o_item_sap
                            .disp_date
                        ).length === 0
                    );
                  }
                );

                dispatch(
                  sendTagging({
                    event: 'sem_estimativa_entrega',
                    screenName: 'carrinho',
                    tipo_compra: tipo_compra,
                    cod_prod: itemWithoutDispDateTagging
                  })
                );
                dispatch(
                  addToast({
                    message: 'Não há estimativa de entrega de alguns produtos.',
                    type: EToastTypes.WARNING,
                    durationInMS: 5000
                  })
                );
              }
            }
          };
          displayWarnings();
        }, 1000);
      } catch {
        setCheckoutState(CHECKOUT_ORDERS_INITIAL_STATE);
        updateCheckoutData(CHECKOUT_ORDERS_INITIAL_STATE);
        setData(CHECKOUT_ORDERS_INITIAL_STATE.data);
        setParams(CHECKOUT_ORDERS_INITIAL_STATE.params);
        setModalError((prev) => ({
          ...prev,
          description:
            'Seu pedido contém informações desatualizadas. Atualize os dados no carrinho para continuar.',
          title: 'Atualize o seu pedido',
          buttonRight: {
            title: 'Atualizar pedido'
          },
          buttonLeft: {
            ...prev.buttonLeft,
            canShow: true
          },
          show: true
        }));
      }
    };

    loadData();
  }, []);

  const loadTaxes = async () => {
    if (taxesData.loading) {
      return null;
    }
    if (!shoppingCart.emitterId || !shoppingCart.receiverId) {
      return null;
    }
    try {
      setTaxesData({ ...taxesData, loading: true, error: false });
      const checkoutServiceMvp = new CheckoutServiceMvp();

      const response = await handleTaxesCalculation({
        checkoutServiceMvp,
        items: (data?.orders?.[0]?.order || []).map((orgIt, orgItIndex) => {
          const it = (params?.items || [])[orgItIndex];
          return {
            ...it,
            isAgreementPricing: orgIt.extension_attributes
              .has_agreed_price_gerdau
              ? 'X'
              : ''
          };
        }),
        receiverId: shoppingCart.receiverId,
        emitterId: shoppingCart.emitterId,
        cartId: cartId || ''
      });

      if (response instanceof Error || !response) {
        throw response;
      }

      const total = [
        {
          title: 'Preço total do pedido',
          value: currencyFormatter({
            amount: Number(response.total_include_tax ?? '0'),
            currency
          }),
          tooltipText:
            'O preço total do pedido considera todos os valores do seu pedido, inclusive impostos e descontos.'
        },
        {
          title: 'Preço total sem impostos',
          value: currencyFormatter({
            amount: Number(response.total_exclude_tax ?? '0'),
            currency
          }),
          tooltipText:
            'O preço total sem impostos considera todos os valores do seu pedido, inclusive descontos mas excluindo impostos.'
        }
      ];

      const convertedObj = {
        o_price_sap_totals: {
          icms: String(response?.icms ?? '0'),
          pis: String(response?.pis ?? '0'),
          cofins: String(response?.cofins ?? '0'),
          ipi: String(response?.ipi ?? '0'),
          icms_st: String(response?.st ?? '0'),
          fcp: String(response?.fcp ?? '0'),
          difal: String(response?.difal ?? '0'),
          price_with_discount_total: String(
            data?.o_price_sap_totals.price_with_discount_total ?? '0'
          ),
          price_net: String(response?.total_exclude_tax ?? '0')
        }
      };

      const taxes = displayTaxes(convertedObj);

      setTaxValues(taxes);
      setTotalValues(total);
      taxesLoadedRef.current = true;
      setTaxesData((prev) => ({
        ...prev,
        data: response
      }));

      if (checkoutState && checkoutState.data) {
        const updatedCheckoutState = {
          ...checkoutState!,
          data: {
            ...checkoutState!.data,
            ...convertedObj
          },
          params: params
        };

        updateCheckoutData(updatedCheckoutState);
      }
    } catch (error) {
      console.error({ taxesData: error });
      setTaxesData((prev) => ({
        ...prev,
        error: true,
        loading: false
      }));

      if (checkoutState && checkoutState.data) {
        const updatedCheckoutState = {
          ...checkoutState,
          data: {
            ...checkoutState.data,
            o_price_sap_totals: {
              ...checkoutState.data.o_price_sap_totals
            }
          },
          params: params
        };

        updateCheckoutData(updatedCheckoutState);
      }
    } finally {
      setTaxesData((prev) => ({
        ...prev,
        loading: false,
        retryCount: prev.retryCount + 1
      }));
    }
  };

  const loadEstimatedDate = async () => {
    if (estimatedDateData.loading || !checkoutState || !checkoutState.data) {
      return null;
    }
    if (!shoppingCart.emitterId || !shoppingCart.receiverId) {
      return null;
    }

    try {
      setEstimatedDateData((prev) => ({
        ...prev,
        loading: true,
        error: false
      }));
      const checkoutServiceMvp = new CheckoutServiceMvp();

      const response = await handleEstimatedDate({
        items: params?.items ?? [],
        checkoutServiceMvp,
        receiverId: shoppingCart.receiverId,
        emitterId: shoppingCart.emitterId
      });

      const orderSimulate = {
        ...checkoutState,
        data: {
          ...checkoutState.data,
          orders: [
            {
              order: checkoutState.data.orders[0].order.map(
                (orderItem, index) => ({
                  ...orderItem,
                  extension_attributes: {
                    ...orderItem.extension_attributes,
                    sap_fields: {
                      ...orderItem.extension_attributes.sap_fields,
                      o_item_sap: {
                        ...orderItem.extension_attributes.sap_fields.o_item_sap,
                        disp_date: response?.orderItems[index]?.confirmedDate
                          ? moment(response.orderItems[index].confirmedDate)
                              .format('DD-MM-YYYY')
                              .toString()
                          : ''
                      }
                    }
                  }
                })
              )
            }
          ]
        }
      };

      setCheckoutState(orderSimulate);
      setData(orderSimulate.data);
      setParams(orderSimulate.params);

      updateCheckoutData({
        ...checkoutState,
        data: orderSimulate.data,
        params: params
      });

      setEstimatedDateData((prev) => ({
        ...prev,
        data: response
      }));
    } catch (error) {
      console.error({ estimatedDate: error });

      // const orderSimulate = {
      //   ...checkoutState,
      //   data: {
      //     ...checkoutState.data,
      //     orders: [
      //       {
      //         order: checkoutState.data.orders[0].order.map((orderItem) => ({
      //           ...orderItem,
      //           extension_attributes: {
      //             ...orderItem.extension_attributes,
      //             sap_fields: {
      //               ...orderItem.extension_attributes.sap_fields,
      //               o_item_sap: {
      //                 ...orderItem.extension_attributes.sap_fields.o_item_sap,
      //                 disp_date: 'api error'
      //               }
      //             }
      //           }
      //         }))
      //       }
      //     ]
      //   }
      // };

      setEstimatedDateData((prev) => ({
        ...prev,
        error: true,
        loading: false
      }));

      // setCheckoutState(orderSimulate);
      // setData(orderSimulate.data);
      // setParams(orderSimulate.params);

      // updateCheckoutData({
      //   ...checkoutState,
      //   data: orderSimulate.data,
      //   params: params
      // });
    } finally {
      setEstimatedDateData((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    if (newCheckoutFlow && params?.items && params?.items.length > 0) {
      loadTaxes();
      loadEstimatedDate();
    }
  }, [params?.items]);

  useEffect(() => {
    if (
      ordersTable.length > 0 &&
      params?.items.length != 0 &&
      ordersTable.flatMap((order) => order.orderItems.length > 0)
    ) {
      dispatch(
        sendTagging({
          event: 'begin_checkout',
          ecommerce: {
            currency: 'BRL',
            value: currencyFormatter({
              amount: Number(
                data?.o_price_sap_totals.price_with_discount_total ?? '0'
              ),
              currency
            }),
            screenName: 'revisar_pedido',
            tipo_compra: tipo_compra,
            bp_emissor: emitterSelected?.customerId,
            bp_recebedor: receiverSelected?.customerId,
            items: ordersTable.flatMap((order) =>
              order.orderItems.map((product) => ({
                screenName: 'revisar_pedido',
                tipo_compra: getTipoCompraForItemId(
                  product.magentoItemId ?? 0,
                  purchaseOrigin(),
                  product.productId
                ),
                item_id: product.productId,
                item_name: product.itemName,
                item_category:
                  params?.items.find((item) => item.sku === product.productId)
                    ?.item_category ?? 'tipo de produto indisponivel',
                price: product.itemPrice,
                unidade_de_medida: getMeasurementUnit(product.itemQuantity),
                quantity: product.itemQuantity,
                data_desejada: product.itemDesiredDate,
                pagamento: product.itemPayment,
                beneficio_fiscal: product.itemTaxBenefit,
                destinacao: product.itemDestiny,
                num_ordem_venda: order.orderNumber + 1,
                preco_sem_impostos: product.itemPrice,
                estimativa_entrega: product.itemDeliveryEstimate.length
                  ? product.itemDeliveryEstimate
                  : 'Sem estimativa',
                favoritado: (checkoutData?.favoritedSkus ?? []).includes(
                  String(product.productId)
                )
              }))
            )
          }
        })
      );
    }
  }, [ordersTable, params]);

  function getMeasurementUnit(value: string): string {
    const spacevalue = value.indexOf(' ');
    return value.slice(spacevalue, value.length).trim();
  }
  useEffect(() => {
    mapOrders();
    const setStopwatch = () => {
      if (data?.cartUpdatedIn) {
        const { startTime, endTime } = calculateStartAndEndTimes(
          data.cartUpdatedIn,
          abandonedCartFlag
        );
        setStartTime(startTime);
        setEndTime(endTime);
      }
    };
    setStopwatch();

    const setQtyOfItems = () => {
      if (data?.orders) {
        let quantity = 0;
        data.orders.forEach((order) => (quantity += order.order.length));
        return quantity;
      }
      return 0;
    };
    const qtyOfItems = setQtyOfItems();
    setQuantityOfItems(qtyOfItems);

    const setTaxes = () => {
      if (taxesLoadedRef.current) return;
      const taxes = displayTaxes(data);
      setTaxValues(taxes);
    };
    setTaxes();

    const setOrderValues = () => {
      if (taxesLoadedRef.current) return;
      const values = [
        {
          title: 'Preço total do pedido',
          value: currencyFormatter({
            amount: Number(
              data?.o_price_sap_totals.price_with_discount_total ?? '0'
            ),
            currency
          }),
          tooltipText:
            'O preço total do pedido considera todos os valores do seu pedido, inclusive impostos e descontos.'
        },
        {
          title: 'Preço total sem impostos',
          value: currencyFormatter({
            amount: Number(data?.o_price_sap_totals.price_net ?? '0'),
            currency
          }),
          tooltipText:
            'O preço total sem impostos considera todos os valores do seu pedido, inclusive descontos mas excluindo impostos.'
        }
      ];
      setTotalValues(values);
    };
    setOrderValues();

    const setTable = () => {
      if (data?.orders) {
        const table: CheckoutTableProps[] = [];
        data.orders.forEach((orders, indexTable) => {
          const orderTable: TableProps[] = orders.order.map((order, index) => ({
            key: index,
            itemPositionNumber:
              order.extension_attributes.item_id_gerdau.length > 0
                ? order.extension_attributes.item_id_gerdau
                : undefined,
            itemName:
              params?.items.find(
                (orderParam) => orderParam.material === order.sku
              )?.name ?? '',
            itemQuantity: `${formattedNumber(
              Number(order.qty)
            )} ${order.extension_attributes.measure_unity_gerdau.toUpperCase()}`,
            itemDesiredDate: formatDate(
              order.extension_attributes.expected_date_gerdau
            ),
            itemDeliveryEstimate: newCheckoutFlow
              ? order.extension_attributes.sap_fields.o_item_sap.disp_date
              : order.extension_attributes.sap_fields
              ? formatDate(
                  order.extension_attributes.sap_fields.o_item_sap.disp_date
                )
              : 'Sem estimativa',
            itemDestiny:
              params?.items.find(
                (param) =>
                  param.material === order.sku &&
                  param.industrialType ===
                    order.extension_attributes.destination_gerdau
              )?.description ?? '',
            itemTaxBenefit: order.extension_attributes.sap_fields.o_item_sap
              .beneficio.length
              ? order.extension_attributes.sap_fields.o_item_sap.beneficio
              : 'Nenhum',
            itemPayment:
              order.extension_attributes.payment_terms_description_gerdau ?? '',
            itemPrice: currencyFormatter({
              amount: Number(
                order.extension_attributes.sap_fields.o_price_sap
                  .price_with_discount_total
              ),
              currency
            }),
            productId: order.sku,
            magentoItemId: order.item_id
          }));

          table.push({
            orderNumber: String(indexTable),
            orderItems: orderTable
          });
        });

        setOrdersTable(table);
      }
    };
    setTable();

    setRenderCount(renderCount + 1);
  }, [data, abandonedCartFlag]);

  const formatAddress = (data: ILocality2): string => {
    if (!data) return '';
    const { address, city, state, zipCode } = data;
    const cityState =
      city && state ? `${city} - ${state}` : city || state || '';
    return [address, cityState, zipCode ? `CEP ${zipCode}` : '']
      .filter(Boolean)
      .join(' | ');
  };

  const receiverAddress = formatAddress(receiverSelected?.locality);

  const emitterDetails = [
    {
      title: 'Razão social do emissor',
      value: emitterSelected?.name ?? ''
    },
    {
      title: 'Endereço do emissor',
      value: formatAddress(emitterSelected?.locality)
    },
    {
      title: 'CNPJ do emissor',
      value: emitterSelected?.taxNumber ?? ''
    },
    {
      title: 'Nº do cliente emissor',
      value: emitterSelected?.customerId ?? ''
    }
  ].filter((detail) => !!detail.value);

  const receiverDetails = [
    {
      title: 'Razão social do recebedor',
      value: receiverSelected?.name ?? ''
    },
    {
      title: 'CNPJ do recebedor',
      value: receiverSelected?.taxNumber ?? ''
    },
    {
      title: 'Nº do cliente recebedor',
      value: receiverSelected?.customerId ?? ''
    }
  ].filter((detail) => !!detail.value);

  const summaryDetails = [
    {
      title: 'Peso total',
      value: params && params.items ? formatTotalWeight(params.items) : '0'
    },
    {
      title: 'Quantidade de itens',
      value: `${quantityOfItems}`
    }
  ].filter((detail) => !!detail.value);

  const mapOrders = async () => {
    if (data?.orders.length && params?.items.length) {
      setIsLoading(true);
      const purchaseId =
        data.orders[0].order[0].extension_attributes.sap_fields.o_header_sap
          .purch_no_c ?? '';
      setCustomerPurchaseId(purchaseId);
      const salesInfo: IItemSaleInfoMvp[] = data.orders
        .flatMap((item) => item.order)
        .map((subItem) => {
          const item = params.items.find((item) =>
            matchingItems(item, subItem)
          );
          return {
            activitySector: item?.activitySector,
            benefit:
              subItem.extension_attributes.sap_fields.o_item_sap.beneficio,
            description: item?.description,
            consumptionOrder:
              item?.description.toLowerCase() === 'consumo' ? 'X' : undefined,
            customerPurchaseItem:
              subItem?.extension_attributes.sap_fields.o_header_sap.purch_no_c,
            distrChannel: Number(
              subItem.extension_attributes.sap_fields.o_header_sap.distr_chan
            ),
            industrialType: item?.industrialType,
            material:
              subItem.extension_attributes.sap_fields.o_item_sap.material,
            plant: Number(
              subItem.extension_attributes.sap_fields.o_item_sap.plant
            ),
            salesGroup:
              subItem.extension_attributes.sap_fields.o_header_sap.sales_grp,
            salesOrg:
              subItem.extension_attributes.sap_fields.o_header_sap.sales_org,
            salesOffice:
              subItem.extension_attributes.sap_fields.o_header_sap.sales_off,
            quantity: subItem.qty,
            requiredDate: subItem?.extension_attributes?.expected_date_gerdau
              ? moment(subItem.extension_attributes.expected_date_gerdau)
                  .format('YYYY-MM-DD')
                  .toString()
              : '',
            sku: subItem.sku,
            term: subItem.extension_attributes.payment_type_gerdau,
            unityMeasure: subItem.extension_attributes.measure_unity_gerdau,
            totalWeightInTon: formatNumberWithLocale({
              value:
                item && item.measureUnits
                  ? convertKgToTon(
                      convertToKg(
                        Number(subItem.qty),
                        item.measureUnits,
                        subItem.extension_attributes.measure_unity_gerdau
                      )
                    )
                  : 0
            })
          };
        });

      const incompleteInformation = salesInfo.find(
        (sales) =>
          !sales.activitySector ||
          !sales.industrialType ||
          !sales.sku ||
          !sales.unityMeasure ||
          !sales.quantity
      );

      if (incompleteInformation) {
        setItemsSalesInfo(salesInfo);
        setIsLoading(false);

        setModalError((prev) => ({
          ...prev,
          description:
            'Seu pedido contém informações desatualizadas. Atualize os dados no carrinho para continuar.',
          title: 'Atualize o seu pedido',
          buttonRight: {
            title: 'Atualizar pedido'
          },
          buttonLeft: {
            ...prev.buttonLeft,
            canShow: true
          },
          show: true
        }));
        return;
      }

      setItemsSalesInfo(salesInfo);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const updateOrderConfirmationStatus = () => {
      if (checkoutState) {
        updateCheckoutData({
          ...checkoutState,
          orderConfirmationRequested,
          ordersTable
        });
      }
    };
    updateOrderConfirmationStatus();
  }, [orderConfirmationRequested]);

  const matchingItems = (
    bodyOrderSimulate: IItemParamOrderSimulate,
    responseOrderSimulate: IItemResponseOrderSimulate
  ) => {
    const isSameSku = bodyOrderSimulate.sku === responseOrderSimulate.sku;
    const isSameBenefit =
      bodyOrderSimulate.benefit ===
        responseOrderSimulate.extension_attributes.sap_fields.o_item_sap
          .beneficio ||
      bodyOrderSimulate.benefit ===
        responseOrderSimulate.extension_attributes.sap_fields.o_item_sap
          .beneficiocode;
    const isSameIndustrialType =
      bodyOrderSimulate.industrialType ===
      responseOrderSimulate.extension_attributes.destination_gerdau;
    const isSamePaymentType =
      bodyOrderSimulate.term ===
      responseOrderSimulate.extension_attributes.payment_type_gerdau;
    const isSameRequiredDate =
      formatDate(bodyOrderSimulate.requiredDate) ===
      formatDate(
        responseOrderSimulate.extension_attributes.expected_date_gerdau
      );
    const isSameUnityMeasure =
      bodyOrderSimulate.unityMeasure ===
      responseOrderSimulate.extension_attributes.measure_unity_gerdau;

    return (
      isSameSku &&
      isSameBenefit &&
      isSameIndustrialType &&
      isSamePaymentType &&
      isSameRequiredDate &&
      isSameUnityMeasure
    );
  };

  const updateTable = (item: ICustomerIdParams) => {
    const dataCopy = { ...checkoutState! };

    if (dataCopy.data && checkoutState?.data) {
      const tempOrders = [...dataCopy.data!.orders];
      const tempOrderTable = tempOrders[Number(item.salesOrderId)];
      const itemTemp = tempOrderTable.order.find(
        (order, index) => order.sku === item.itemId && index === item.index
      );
      if (
        !itemTemp ||
        itemTemp.extension_attributes.item_id_gerdau == item.customerId
      ) {
        return;
      }

      itemTemp.extension_attributes.item_id_gerdau = item.customerId;

      setCheckoutState({
        ...checkoutState,
        data: { ...checkoutState!.data, orders: tempOrders }
      });

      const newItemPositionNumber =
        item.customerId !== '' ? item.customerId : undefined;
      setOrdersTable((prev) =>
        prev.map((order) => {
          if (order.orderNumber !== item.salesOrderId) return order;
          return {
            ...order,
            orderItems: order.orderItems.map((orderItem) =>
              orderItem.key === item.index
                ? { ...orderItem, itemPositionNumber: newItemPositionNumber }
                : orderItem
            )
          };
        })
      );
    }
  };

  const updateCustomerId = (customerId: string) => {
    if (newCheckoutFlow) {
      setCustomerPurchaseId(customerId);
    }
    if (customerId === customerPurchaseId) {
      return;
    }

    const dataCopy = { ...checkoutState! };
    if (dataCopy.data && checkoutState?.data) {
      const tempOrders = [...dataCopy.data.orders];
      tempOrders.forEach((orders) => {
        orders.order.forEach((order) => {
          order.extension_attributes.sap_fields.o_header_sap.purch_no_c =
            customerId;
        });
      });

      setCheckoutState({
        ...checkoutState,
        data: { ...checkoutState!.data, orders: tempOrders }
      });
      setData(dataCopy.data);
    }
  };

  useEffect(() => {
    if (errorOrdersSent) {
      handleClickTagging('erro_credito_expirado', 'finalizar_pedido');
    }
    setModalError({ ...MODAL_ERROR_DEFAULT, show: errorOrdersSent });
  }, [errorOrdersSent]);

  const addParagraphBeforeLastDiv = (htmlString: string) => {
    const lastDivIndex = htmlString.lastIndexOf('</div>');
    const tagNotFound = lastDivIndex === -1;
    if (tagNotFound) {
      return htmlString;
    }
    const additionalContent = newCheckoutFlow
      ? '<p>O preço e a estimativa de entrega podem sofrer alterações.<br/>Fale com o seu vendedor.</p>'
      : '<p>O preço e a estimativa de entrega podem sofrer oscilações.</p>';
    const newHtmlString =
      htmlString.slice(0, lastDivIndex) +
      additionalContent +
      htmlString.slice(lastDivIndex);

    return newHtmlString;
  };

  const hasItemsWithoutRequiredDate = (): boolean =>
    itemsSalesInfo.some((item) => !item.requiredDate);

  /**
   * Aciona action que irá confirmar o pedido
   */
  const sendOrders = async () => {
    try {
      setIsLoading(true);

      if (hasItemsWithoutRequiredDate()) {
        dispatch(
          addToast({
            type: EToastTypes.DANGER,
            message: 'Serviço indisponível temporariamente, tente novamente',
            durationInMS: 5000
          })
        );
        goBack();
        return;
      }

      if (!shoppingCart.emitterId || !shoppingCart.receiverId) {
        dispatch(
          addToast({
            type: EToastTypes.DANGER,
            message: 'Serviço indisponível temporariamente, tente novamente',
            durationInMS: 5000
          })
        );
        goBack();
        return;
      }

      // salva para a possibilidade de ter que gerar PDF
      const infoContentHtml = orderInfoRef.current?.innerHTML;
      const tableHtml = addParagraphBeforeLastDiv(
        orderTableRef.current?.innerHTML ?? ''
      );

      const salesParams: ISalesParams = {
        emitterId: shoppingCart.emitterId,
        receiverId: shoppingCart.receiverId,
        data: itemsSalesInfo,
        customerOrder: customerPurchaseId,
        cartNumber: checkoutState!.params!.cartNumber,
        isNewCheckoutFlow: newCheckoutFlow,
        taxes: buildTaxesPayload(taxesData.data)
      };

      const checkoutServiceMvp = new CheckoutServiceMvp();
      const data: string[] = await checkoutServiceMvp.sendOrder(salesParams);
      if (data) {
        const updatedCheckoutState = {
          ...checkoutState!,
          orderConfirmationRequested,
          ordersTable,
          pdf: { text: infoContentHtml!, table: tableHtml! },
          orderCompleted: true
        };

        setCheckoutState(updatedCheckoutState);
        updateCheckoutData(updatedCheckoutState);

        // pedido enviado e informações do pedido salvas
        dispatch(updateCartItemsQuantity(0));
        history.replace(`${EOrderSettingsRoutes.FINALIZE_ORDER}`);
      }
    } catch (e) {
      setModalError((prev) => ({
        ...prev,
        description:
          'Seu pedido contém informações desatualizadas. Atualize os dados no carrinho para continuar.',
        title: 'Atualize o seu pedido',
        buttonRight: {
          title: 'Atualizar pedido'
        },
        buttonLeft: {
          ...prev.buttonLeft,
          canShow: true
        },
        show: true
      }));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Atualiza o item_id e aciona action que irá confirmar o pedido
   */
  const sendOrdersUpdated = async () => {
    // salva para a possibilidade de ter que gerar PDF
    try {
      setIsLoading(true);

      if (hasItemsWithoutRequiredDate()) {
        dispatch(
          addToast({
            type: EToastTypes.DANGER,
            message: 'Serviço indisponível temporariamente, tente novamente',
            durationInMS: 5000
          })
        );
        goBack();
        return;
      }

      if (!shoppingCart.emitterId || !shoppingCart.receiverId) {
        dispatch(
          addToast({
            type: EToastTypes.DANGER,
            message: 'Serviço indisponível temporariamente, tente novamente',
            durationInMS: 5000
          })
        );
        goBack();
        return;
      }

      const infoContentHtml = orderInfoRef.current?.innerHTML;
      const tableHtml = addParagraphBeforeLastDiv(
        orderTableRef.current?.innerHTML ?? ''
      );

      const orderItems = data?.orders.flatMap((item) => item.order) ?? [];
      const items = orderItems.map(
        (item, index) =>
          ({
            ...item,
            qty: Number(item.qty),
            extension_attributes: {
              ...item.extension_attributes,
              ...(estimatedDateData.data?.orderItems[index]?.confirmedDate && {
                estimated_date: moment(
                  estimatedDateData.data.orderItems[index].confirmedDate
                ).format('YYYY-MM-DD')
              })
            }
          } as unknown as NewCartItem)
      );

      const response = await addBulkGetCart({
        items,
        withOptions: false,
        withoutMimimum: true,
        withPrice: false
      });

      if (!response) {
        throw new Error();
      }

      const { hasErrors: hasBulkErrors } = dispatchBulkErrorToast(
        response.cart?.items ?? [],
        response.errors ?? []
      );
      if (hasBulkErrors) {
        throw new Error();
      }

      const salesParams: ISalesParams = {
        emitterId: shoppingCart.emitterId,
        receiverId: shoppingCart.receiverId,
        data: itemsSalesInfo,
        customerOrder: customerPurchaseId,
        cartNumber: checkoutState!.params!.cartNumber,
        isNewCheckoutFlow: newCheckoutFlow,
        taxes: buildTaxesPayload(taxesData.data)
      };

      const checkoutServiceMvp = new CheckoutServiceMvp();
      const resp: string[] = await checkoutServiceMvp.sendOrder(salesParams);
      if (resp) {
        const updatedCheckoutState = {
          ...checkoutState!,
          orderConfirmationRequested,
          ordersTable,
          pdf: { text: infoContentHtml!, table: tableHtml! },
          orderCompleted: true
        };

        setCheckoutState(updatedCheckoutState);
        updateCheckoutData(updatedCheckoutState);

        // pedido enviado e informações do pedido
        dispatch(updateCartItemsQuantity(0));
        history.replace(`${EOrderSettingsRoutes.FINALIZE_ORDER}`);
      }
    } catch (error) {
      setModalError((prev) => ({
        ...prev,
        description:
          'Seu pedido contém informações desatualizadas. Atualize os dados no carrinho para continuar.',
        title: 'Atualize o seu pedido',
        buttonRight: {
          title: 'Atualizar pedido'
        },
        buttonLeft: {
          ...prev.buttonLeft,
          canShow: true
        },
        show: true
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const setTableValidity = (isValid: boolean) => {
    setTableIsValid(isValid);
  };

  const modalButtonRight = () => {
    if (modalError.buttonRight.title === 'Atualizar pedido') {
      dispatch(
        sendTagging({
          event: 'modal_atualizar_pedido',
          screenName: 'carrinho',
          etapa: 'atualizar_pedido'
        })
      );
    }
    setModalError(MODAL_ERROR_DEFAULT);
    goBack();
  };

  const modalButtonLeft = () => {
    setModalError(MODAL_ERROR_DEFAULT);
    dispatch(
      sendTagging({
        event: 'modal_atualizar_pedido',
        screenName: 'carrinho',
        etapa: 'cancelar_pedido'
      })
    );
    goToPage(`${ERoutes.PURCHASE}/commerce/${EOrdersRoutes.STEEL_TYPE_CHOOSE}`);
  };

  return (
    <Box>
      <div ref={orderInfoRef}>
        <CheckoutOrder
          orderDetails={emitterDetails}
          receiverDetails={receiverDetails}
          summaryDetails={summaryDetails}
          orderValues={totalValues}
          orderTax={taxValues}
          orderPaymentDescription={
            data?.orders[0]?.order[0]?.extension_attributes
              ?.payment_terms_description_gerdau || ''
          }
          updateCustomerId={updateCustomerId}
          onLoadTaxes={loadTaxes}
          receiverAddress={receiverAddress}
          taxesData={taxesData}
        />
      </div>
      {startTimeState && endTimeState && (
        <CountDownProgress
          {...{ startTime: startTimeState, endTime: endTimeState }}
        />
      )}
      <div ref={orderTableRef}>
        <CheckoutTable
          data={ordersTable}
          totalPrice={currencyFormatter({
            amount: Number(
              data?.o_price_sap_totals.price_with_discount_total ?? '0'
            ),
            currency
          })}
          estimatedDate={estimatedDateData}
          setTableValidity={setTableValidity}
          updateTableItem={updateTable}
        />
      </div>

      <BottomActionBar
        item={{
          title: `Preço total do pedido: ${currencyFormatter({
            amount: Number(
              data?.o_price_sap_totals.price_with_discount_total ?? '0'
            ),
            currency
          })}`,
          value: 'O preço e a estimativa de entrega podem sofrer alterações.',
          ...(newCheckoutFlow && {
            secondValue: 'Fale com o seu vendedor.'
          }),
          placement: 'topLeft',
          ...(!newCheckoutFlow && {
            tooltipText:
              'O preço total do pedido considera todos os valores do seu pedido, inclusive impostos e descontos.'
          })
        }}
        primaryBtn={{
          title: 'Finalizar pedido',
          primaryEvent: newCheckoutFlow ? sendOrdersUpdated : sendOrders,
          disabled:
            !tableIsValid ||
            !data?.orders ||
            buttonLoading ||
            desabilitarFinalizarPedido,
          loading: buttonLoading
        }}
        secondaryBtn={{
          title: 'Alterar Pedido',
          secondaryEvent: () => goBack()
        }}
      />
      <LoadingModal
        enabled={loading}
        title="Aguarde alguns instantes"
        message="Finalizando o pedido ..."
      />
      <ErrorModal
        description={modalError.description}
        title={modalError.title}
        buttonLeft={{
          ...modalError.buttonLeft,
          onClick: modalButtonLeft
        }}
        buttonRight={{ ...modalError.buttonRight, onClick: modalButtonRight }}
        show={modalError.show}
      ></ErrorModal>
    </Box>
  );
};

export default CheckoutMVP;
